import { useFeatureFlags } from '@chronosphereio/core';
import { useCallback, useMemo } from 'react';
import { useIsOnScreen } from '../../plugin-system/runtime/in-view';
import { usePanelTooltipActions, usePanelTooltipStore } from '../PanelTooltip/PanelTooltipProvider';

/**
 * Broadcast the shared crosshair timestamp.
 * The active panel calls `broadcastTimestamp` from its mouse handler after
 * converting the cursor position to a timestamp.
 *
 * The `enableUnifiedTimeAxisCrosshair` feature flag is checked here at the
 * broadcast level only. When the flag is off, `syncGroup` is broadcast as
 * `undefined`, so no receiver's group will match and all crosshairs stay hidden.
 */
export function useCrosshairBroadcast(syncGroup: string | undefined) {
  const { enableUnifiedTimeAxisCrosshair } = useFeatureFlags();
  const setCrosshairTimestamp = usePanelTooltipStore((state) => state.setCrosshairTimestamp);

  const crosshairSyncGroup = enableUnifiedTimeAxisCrosshair ? syncGroup : undefined;

  const broadcastTimestamp = useCallback(
    (timestampMs: number | undefined) => {
      if (crosshairSyncGroup === undefined) return;
      setCrosshairTimestamp(timestampMs, crosshairSyncGroup);
    },
    [setCrosshairTimestamp, crosshairSyncGroup]
  );

  return { broadcastTimestamp };
}

/**
 * Determine the pixel-X position of the shared crosshair timestamp.
 * Return `undefined` if the crosshair should not be shown (wrong sync group, off-screen, etc.).
 *
 * @param syncGroup - This panel's sync group (must match the broadcaster's group).
 * @param timestampToPixelX - Panel-specific callback that converts a timestamp
 *   to a local pixel-X coordinate within the plot area.
 */
export function useCrosshairReceiver({
  syncGroup,
  timestampToPixelX,
}: {
  syncGroup: string | undefined;
  timestampToPixelX: (timestampMs: number) => number | undefined;
}) {
  const onScreenCtx = useIsOnScreen();
  const isOnScreen = onScreenCtx === undefined ? true : onScreenCtx.isInView;

  const shouldSubscribe = isOnScreen && syncGroup !== undefined;
  const crosshairTimestampMs = usePanelTooltipStore((state) =>
    shouldSubscribe ? state.crosshairTimestampMs : undefined
  );
  const crosshairSyncGroup = usePanelTooltipStore((state) => (shouldSubscribe ? state.crosshairSyncGroup : undefined));

  return useMemo(() => {
    if (
      !isOnScreen ||
      crosshairTimestampMs === undefined ||
      syncGroup === undefined ||
      crosshairSyncGroup !== syncGroup
    ) {
      return undefined;
    }

    return timestampToPixelX(crosshairTimestampMs);
  }, [isOnScreen, crosshairTimestampMs, crosshairSyncGroup, syncGroup, timestampToPixelX]);
}

/**
 * Wire a panel into the shared crosshair system.
 * Combines broadcasting, receiving, and active-panel management.
 * Callers are responsible for resolving the sync group before passing it in.
 *
 * @param syncGroup - Pre-resolved sync group. `undefined` opts out of syncing.
 * @param panelKey - Unique key for this panel instance (used for active-panel tracking).
 * @param timestampToPixelX - Panel-specific callback converting a timestamp to a local
 *   pixel-X coordinate within the plot area (wrap in `useCallback`).
 */
export function usePanelCrosshair({
  syncGroup,
  panelKey,
  timestampToPixelX,
}: {
  syncGroup: string | undefined;
  panelKey: string;
  timestampToPixelX: (ts: number) => number | undefined;
}) {
  const { broadcastTimestamp } = useCrosshairBroadcast(syncGroup);
  const { setActivePanelKey } = usePanelTooltipActions();
  const crosshairPixelX = useCrosshairReceiver({ syncGroup, timestampToPixelX });

  const onMouseEnter = useCallback(() => {
    setActivePanelKey(panelKey);
  }, [setActivePanelKey, panelKey]);

  const onMouseLeave = useCallback(() => {
    setActivePanelKey(undefined);
  }, [setActivePanelKey]);

  return {
    crosshairPixelX,
    broadcastTimestamp,
    crosshairHandlers: { onMouseEnter, onMouseLeave },
  };
}
